import React from 'react';
import type { ShowcasePopulatedItem, ItemImage } from 'root/apiTypes';
import { st, classes } from './Item.st.css';
import { useItemLogic } from './Item.logic';
import { Labels } from 'root/components/Menus/Labels/Labels';
import { PriceVariants } from 'root/components/Menus/PriceVariants/PriceVariants';
import { Button, ButtonPriority, HoverStyles, Text } from 'wix-ui-tpa/cssVars';
import { dataHooks } from 'root/dataHooks';
import { classes as global } from 'root/components/Menus/Global.st.css';
import { Dots } from 'root/components/Menus/Dots/Dots';
import { ModifierGroups } from 'root/components/Menus/ModifierGroups/ModifierGroups';
import { FeaturedBadge } from 'root/components/Menus/FeaturedBadge/FeaturedBadge';
import { Image } from 'root/components/Menus/Image/Image';
import type { ItemAction } from 'root/contexts/ItemsContext';

export type ItemProps = {
  item: ShowcasePopulatedItem;
};

const ItemActionButton = ({ action, item }: { action: ItemAction; item: ShowcasePopulatedItem }) => {
  const isDisabled = action.disabled || item.orderSettings?.inStock === false;

  return (
    <Button
      upgrade
      aria-label={action.ariaLabel}
      disabled={isDisabled}
      fullWidth={action.fullWidth ?? true}
      hoverStyle={HoverStyles.colorChange}
      prefixIcon={action.prefixIcon}
      priority={ButtonPriority.primary}
      style={action.buttonStyle}
      type="button"
      className={st(
        classes.addToCartButton,
        action.fullWidth === false ? classes.addToCartButtonFitToText : '',
        action.showLabel === false ? classes.addToCartButtonIconOnly : ''
      )}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => {
        if (isDisabled) {
          return;
        }

        void action.onClick(item);
      }}
    >
      {action.showLabel === false ? '' : action.label}
    </Button>
  );
};

export const Item = ({ item }: ItemProps) => {
  const {
    hasPriceVariants,
    isMarketPrice,
    priceContent,
    shouldShowPrice,
    shouldShowLabels,
    shouldShowModifierGroups,
    itemImage,
    itemTitleHtmlTag,
    itemDescriptionHtmlTag,
    itemPriceHtmlTag,
    shouldOverrideItemDesign,
    isFeaturedItem,
    shouldReserveImageContainer,
    discountLabel,
    discountedPrice,
    variantDiscounts,
    cartQuantity,
    itemAction,
    itemClassName,
    disableImageExpand,
    itemImageResize,
  } = useItemLogic({ item });

  return (
    <div className={classes.root} data-hook={dataHooks.item.root} role="listitem">
      <div
        data-hook={dataHooks.item.container}
        className={st(
          classes.itemContainer,
          itemImage || shouldReserveImageContainer ? '' : classes.noImage,
          shouldOverrideItemDesign ? classes.featured : '',
          hasPriceVariants || !shouldShowPrice ? classes.noPrice : '',
          itemClassName
        )}
      >
        <div className={classes.itemMain}>
          {isFeaturedItem && <FeaturedBadge featuredType={item.featuredInfo?.type} />}
          <div className={st(global.fullWidth, classes.itemNameContainer)} data-hook={dataHooks.item.nameContainer}>
            <Text className={classes.itemName} data-hook={dataHooks.item.name} tagName={itemTitleHtmlTag}>
              {cartQuantity > 0 && (
                <span className={classes.cartQuantityBadge} data-hook={dataHooks.item.cartQuantity}>
                  {cartQuantity}
                </span>
              )}
              {item.name}
            </Text>
          </div>
          {!hasPriceVariants && shouldShowPrice && <Dots />}
          {item.description && (
            <Text
              className={st(global.fullWidth, classes.itemDescription)}
              data-hook={dataHooks.item.description}
              tagName={itemDescriptionHtmlTag}
            >
              {item.description}
            </Text>
          )}
          {hasPriceVariants ? (
            discountLabel ? (
              <div className={classes.priceVariantsWithLabel} data-hook={dataHooks.item.priceVariantsWithLabel}>
                <PriceVariants variantDiscounts={variantDiscounts} variants={item.priceVariants?.variants ?? []} />
                <Text className={classes.discountLabel} data-hook={dataHooks.item.discountLabel}>
                  {discountLabel}
                </Text>
              </div>
            ) : (
              <PriceVariants variants={item.priceVariants?.variants ?? []} />
            )
          ) : (
            shouldShowPrice && (
              <div
                className={st(global.fullWidth, classes.itemPriceContainer)}
                data-hook={dataHooks.item.priceContainer}
              >
                {!isMarketPrice && discountedPrice ? (
                  <div className={classes.discountedPriceRow}>
                    <Text
                      className={st(classes.itemPrice, classes.originalPrice)}
                      data-hook={dataHooks.item.price}
                      tagName={itemPriceHtmlTag}
                    >
                      {priceContent}
                    </Text>
                    <Text
                      className={classes.itemPrice}
                      data-hook={dataHooks.item.discountedPrice}
                      tagName={itemPriceHtmlTag}
                    >
                      {discountedPrice}
                    </Text>
                  </div>
                ) : (
                  <Text className={classes.itemPrice} data-hook={dataHooks.item.price} tagName={itemPriceHtmlTag}>
                    {priceContent}
                  </Text>
                )}
              </div>
            )
          )}
          {!hasPriceVariants && shouldShowPrice && !isMarketPrice && discountedPrice && discountLabel && (
            <Text
              className={st(classes.discountLabel, classes.itemDiscountLabel)}
              data-hook={dataHooks.item.discountLabel}
            >
              {discountLabel}
            </Text>
          )}
          {shouldShowLabels && <Labels data-hook={dataHooks.item.labels} labels={item.labels!} />}
          {shouldShowModifierGroups && <ModifierGroups modifierGroups={item.modifierGroups ?? []} />}

          {itemImage ? (
            <Image<ItemImage>
              additionalImages={(item.additionalImages as ItemImage[]) ?? []}
              alt={item.image?.altText || item.name}
              dataHook={dataHooks.item.image}
              image={itemImage}
              imageClassName={classes.itemImage}
              resize={itemImageResize}
              shouldDisableExpand={disableImageExpand}
            />
          ) : null}
          {shouldReserveImageContainer && (
            <div className={classes.reservedImageContainer} data-hook={dataHooks.item.reservedImageContainer} />
          )}
        </div>
        {itemAction && (
          <div className={classes.addToCartContainer} data-hook={dataHooks.item.addToCartContainer}>
            <ItemActionButton action={itemAction} item={item} />
          </div>
        )}
      </div>
    </div>
  );
};
